import { Check } from 'lucide-react';
import { ONBOARDING_STAGES } from './onboardingStages';

/**
 * Sprint 40 — one shared progress bar for the whole signup→attending-
 * customers journey. Rendered by BOTH OnboardingWizard.js (stages 0-2) and
 * LeadAttendanceSetup.js (stages 3-6) so the tenant never feels dropped
 * into a second, unrelated wizard halfway through.
 *
 * @param {string} currentKey - key of the stage currently active
 * @param {Set<string>} [completedKeys] - keys already finished (checkmark)
 * @param {(key: string) => void} [onStepClick] - only called for keys the
 *   parent marks reachable by including them in `clickableKeys`
 * @param {Set<string>} [clickableKeys]
 */
export default function OnboardingStepper({ currentKey, completedKeys, onStepClick, clickableKeys }) {
  const completed = completedKeys || new Set();
  const clickable = clickableKeys || new Set();

  return (
    <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-1 -mx-1 px-1" data-testid="onboarding-stepper">
      {ONBOARDING_STAGES.map((s, i) => {
        const isCurrent = s.key === currentKey;
        const isComplete = completed.has(s.key);
        const isClickable = clickable.has(s.key) && onStepClick;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex items-center flex-shrink-0">
            <button
              type="button"
              disabled={!isClickable}
              onClick={() => isClickable && onStepClick(s.key)}
              className={`flex flex-col items-center gap-1 px-2 min-w-[64px] ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              data-testid={`onboarding-stepper-${s.key}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                isComplete ? 'bg-[#F72585] border-[#F72585] text-white' :
                isCurrent ? 'border-[#F72585] text-[#F72585]' :
                'border-[#E2E8F0] text-[#CBD5E1]'
              }`}>
                {isComplete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-semibold whitespace-nowrap ${
                isCurrent || isComplete ? 'text-[#F72585]' : 'text-[#CBD5E1]'
              }`}>{s.title}</span>
            </button>
            {i < ONBOARDING_STAGES.length - 1 && (
              <div className={`h-0.5 w-4 sm:w-6 flex-shrink-0 ${
                completed.has(ONBOARDING_STAGES[i + 1].key) || completed.has(s.key) ? 'bg-[#F72585]' : 'bg-[#E2E8F0]'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}
